/**
 * Gear Page
 *
 * Asset management page for viewing and managing gear inventory
 */

import { useState } from 'react';
import { ChevronDown, Search, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import Chart from 'react-apexcharts';

interface GearItem {
    id: string;
    name: string;
    brand: string;
    group: string;
    category: string;
    subCategory: string;
    type: string;
    status: 'Good' | 'Damaged' | 'Maintenance';
}

const mockGearData: GearItem[] = [
    {
        id: '1',
        name: 'TR-1 Reconnaissance Drone',
        brand: 'TR-1 Reconnaissance',
        group: 'Drone',
        category: 'Gear',
        subCategory: 'UAV',
        type: 'Weapon',
        status: 'Good'
    },
    {
        id: '2',
        name: 'TR-2 Reconnaissance Drone',
        brand: 'TR-2 Reconnaissance',
        group: 'Drone',
        category: 'Gear',
        subCategory: 'UAV',
        type: 'Surveillance',
        status: 'Damaged'
    }
];

export function GearPage() {
    const [filters, setFilters] = useState({
        filter1: false,
        filter2: false,
        filter3: false,
        filter4: false
    });

    const toggleFilter = (filterKey: keyof typeof filters) => {
        setFilters(prev => ({
            ...prev,
            [filterKey]: !prev[filterKey]
        }));
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Good':
                return 'text-emerald-400';
            case 'Damaged':
                return 'text-red-400';
            case 'Maintenance':
                return 'text-yellow-400';
            default:
                return 'text-slate-400';
        }
    };

    const groupsChartOptions = {
        chart: {
            type: 'bar' as const,
            toolbar: { show: false },
            sparkline: { enabled: false }
        },
        colors: ['#3b82f6'],
        plotOptions: {
            bar: {
                horizontal: true,
                dataLabels: { position: 'top' }
            }
        },
        xaxis: {
            categories: ['Drone', 'Camera', 'Battery', 'Sensor', 'UAV'],
            labels: { style: { colors: '#94a3b8', fontSize: '12px' } }
        },
        yaxis: {
            labels: { style: { colors: '#94a3b8', fontSize: '12px' } }
        },
        grid: {
            borderColor: '#334155',
            xaxis: {
                lines: {
                    show: true
                }
            },
            yaxis: {
                lines: {
                    show: false
                }
            }
        },
        tooltip: { theme: 'dark' }
    };

    const groupsChartSeries = [
        {
            name: 'Count',
            data: [45, 50, 30, 25, 40]
        }
    ];

    const assetStatusChartOptions = {
        chart: {
            type: 'donut' as const,
            toolbar: { show: false },
            events: {
                mounted: (chart: { legend: { legendHolder: HTMLElement } }) => {
                    const legend = chart.legend.legendHolder;
                    document.querySelector("#assetStatusLegend")?.appendChild(legend);
                },
                updated: (chart: { legend: { legendHolder: HTMLElement } }) => {
                    const legend = chart.legend.legendHolder;
                    document.querySelector("#assetStatusLegend")?.appendChild(legend);
                }
            }
        },
        colors: ['#10b981', '#ef4444'],
        labels: ['Good', 'Damaged'],
        legend: {
            show: true,
            position: 'right' as const,
            floating: false,
            fontSize: '14px',
            offsetY: 50,
            labels: { colors: '#94a3b8' },
        },
        plotOptions: {
            pie: {
                donut: { size: '55%' }
            }
        },
        dataLabels: { enabled: false },
        stroke: { show: false },
        tooltip: { theme: 'dark' }
    };

    const assetStatusChartSeries = [75, 25];

    return (
        <div className="w-full h-full bg-slate-950 p-6 flex flex-col space-y-6">
            <div className="flex flex-row w-full gap-4">
                {/* Widgets Section */}
                <div
                    className="h-full w-[50%] pointer-events-auto"
                    style={{ right: 'calc(24rem + 1rem + 1rem)' }}>
                    <div className={cn(
                        'bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg shadow-2xl',
                        'h-full overflow-hidden flex flex-col p-3'
                    )}
                    >
                        {/* Telemetry Grid - First Row */}
                        <div className='text-white text-lg mb-2 ml-1'>
                            Assets
                        </div>
                        <div className="grid grid-cols-3 gap-3 h-[35%] mt-4">
                            {/* Call Sign */}
                            <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                    <span className="text-xs text-blue-600">Total Asset</span>
                                </div>
                                <p className="flex-1 flex items-center text-xs font-medium text-white">99</p>
                            </div>

                            {/* Temperature */}
                            <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                    <span className="text-xs text-green-300">Good Asset</span>
                                </div>
                                <p className="flex-1 flex items-center text-xs font-medium text-white">50</p>
                            </div>

                            {/* Wind Speed */}
                            <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                    <span className="text-xs text-red-400">Damaged Asset</span>
                                </div>
                                <p className="flex-1 flex items-center text-xs font-medium text-white">49</p>
                            </div>
                        </div>

                        {/* Second Row */}
                        <div className="grid grid-cols-3 gap-3 h-[35%] mt-3">
                            {/* Speed - Moved below Call Sign */}
                            <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                    <span className="text-xs text-slate-400">Total Group</span>
                                </div>
                                <p className="flex-1 flex items-center text-xs font-medium text-white">22</p>
                            </div>

                            {/* Altitude */}
                            <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                    <span className="text-xs text-slate-400">Category</span>
                                </div>
                                <p className="flex-1 flex items-center text-xs font-medium text-white">15</p>
                            </div>

                            {/* Heading */}
                            <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                    <span className="text-xs text-slate-400">Type</span>
                                </div>
                                <p className="flex-1 flex items-center text-xs font-medium text-white">8</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-2 gap-4 mb-6 h-full w-[50%]">
                    {/* Groups Chart */}
                    <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 h-full">
                        <h3 className="text-white font-semibold mb-4">Groups</h3>
                        <Chart
                            options={groupsChartOptions}
                            series={groupsChartSeries}
                            type="bar"
                            height={200}
                        />
                    </div>

                    {/* Asset Status Chart */}
                    <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 h-full">
                        <h3 className="text-white font-semibold mb-4">Asset Status</h3>
                        <Chart
                            options={assetStatusChartOptions}
                            series={assetStatusChartSeries}
                            type="donut"
                            height={200}
                        />
                    </div>
                </div>

            </div>

            {/* Filters and Create Asset Button */}
            <div className='flex flex-row justify-between items-center gap-4'>
                <div className="flex items-center gap-4">
                    <div className="relative flex items-center">
                        <Search className="absolute left-3 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search..."
                            // value={searchQuery}
                            // onChange={handleSearchChange}
                            className="pl-10 pr-4 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        {[1, 2, 3, 4].map((num) => (
                            <button
                                key={num}
                                onClick={() => toggleFilter(`filter${num}` as keyof typeof filters)}
                                className={cn(
                                    'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                                    filters[`filter${num}` as keyof typeof filters]
                                        ? 'bg-slate-700 text-white'
                                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                                )}
                            >
                                <div className="w-3 h-3 bg-slate-600 rounded"></div>
                                <span>Filter {num}</span>
                                <ChevronDown size={14} />
                            </button>
                        ))}
                    </div>
                </div>

                {/* Create Asset Button */}
                <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors">
                    <Plus size={16} />
                    <span>New Asset</span>
                </button>
            </div>

            {/* Table Container */}
            <div className="flex-1 bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden flex flex-col">
                {/* Table Header */}
                <div className="grid grid-cols-9 gap-0 bg-slate-800 border-b border-slate-700 px-4 py-3 text-xs font-semibold text-slate-300 sticky top-0">
                    <div>Name</div>
                    <div>Brand</div>
                    <div>Group</div>
                    <div>Category</div>
                    <div>Sub-Category</div>
                    <div>Type</div>
                    <div>Status</div>
                    <div>Action</div>
                    <div></div>
                </div>

                {/* Table Body */}
                <div className="flex-1 overflow-y-auto">
                    {mockGearData.map((item) => (
                        <div
                            key={item.id}
                            className="grid grid-cols-9 gap-0 border-b border-slate-800 px-4 py-3 text-sm text-slate-300 hover:bg-slate-800/50 transition-colors items-center"
                        >
                            <div className="text-white font-medium truncate">{item.name}</div>
                            <div className="truncate">{item.brand}</div>
                            <div className="truncate">{item.group}</div>
                            <div className="truncate">{item.category}</div>
                            <div className="truncate">{item.subCategory}</div>
                            <div className="truncate">{item.type}</div>
                            <div className={cn('font-medium', getStatusColor(item.status))}>
                                {item.status}
                            </div>
                            <div>
                                <button className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                                    Details
                                </button>
                            </div>
                            <div></div>
                        </div>
                    ))}
                </div>

                {/* Footer / Pagination */}
                <div className=" border-slate-700 px-4 py-3 flex items-center justify-between text-xs text-slate-400">
                    <span>Showing 1 to 2 of 2 items</span>
                    <div className="flex items-center gap-2">
                        <button className="px-2 py-1 hover:bg-slate-700 rounded transition-colors">‹</button>
                        <button className="px-2 py-1 bg-blue-600 text-white rounded transition-colors">1</button>
                        <button className="px-2 py-1 hover:bg-slate-700 rounded transition-colors">›</button>
                        <button className="px-2 py-1 hover:bg-slate-700 rounded transition-colors">⋙</button>
                        <select className="bg-slate-700 text-white text-xs rounded px-2 py-1 border border-slate-600">
                            <option>1</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
}